import { useEffect, useState } from 'react';
import type { SearchFinanceEntriesInput, SearchFinanceEntriesResult } from '@etiquetachef/types';
import { searchFinanceEntriesCallable } from '@/lib/callables';

/**
 * Busca server-side do extrato (PR B #74). Estado em 3 formas; `refreshNonce`
 * força re-fetch após create/update/delete. Espelha `useTenantsSearch`.
 */
export type FinanceEntriesSearchState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; result: SearchFinanceEntriesResult }
  | { kind: 'ERROR'; error: unknown };

/**
 * Busca server-side paginada dos lançamentos do Financeiro interno, via callable
 * `searchFinanceEntries`.
 *
 * Origem: #74 — 2026-06-21 20:47 +0200 (Financeiro PR B — Lançamentos).
 *
 * Por quê: filtros e saldo são calculados no servidor (`apps/functions/src/finance/
 * searchFinanceEntries.ts`, Admin SDK) — o cliente não lê `finance_entries` direto.
 * `refreshNonce` força re-fetch depois de create/update/delete, porque a callable é snapshot.
 * Espelha `useTenantsSearch`.
 *
 * Contrato: `LOADING | READY | ERROR`; a chave do effect é `JSON.stringify(input)` — objeto
 * de input recriado a cada render não dispara nova busca, mas a ORDEM das chaves conta.
 * Resposta de uma busca já substituída é descartada (`cancelled`).
 *
 * Usado por: `FinanceEntriesPage` e `FinanceImportFlowPage` (`apps/admin`).
 *
 * Dados: `SearchFinanceEntriesInput`/`Result` em `packages/types/src/finance.ts` (valores em
 * centavos).
 */
export function useFinanceEntriesSearch(
  input: SearchFinanceEntriesInput,
  refreshNonce: number,
): FinanceEntriesSearchState {
  const [state, setState] = useState<FinanceEntriesSearchState>({ kind: 'LOADING' });
  const key = JSON.stringify(input);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'LOADING' });
    searchFinanceEntriesCallable(input)
      .then((result) => {
        if (!cancelled) setState({ kind: 'READY', result });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ kind: 'ERROR', error });
      });
    return (): void => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshNonce]);

  return state;
}
